import type { ComponentType } from "react";
import type { CmmdBoardTask, CmmdProjectMilestone } from "@cmmd-forge/contracts";
import { ArchiveIcon, CalendarRangeIcon, FlagIcon, KanbanIcon, LayoutListIcon } from "lucide-react";

import {
  PlanningNewButton,
  PlanningSearchInput,
  PlanningViewSwitcher,
} from "~/components/data-views/PlanningViewChrome";
import { RESPONSIVE_ICON_BUTTON_CLASS, RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";
import type { DataViewController } from "~/views/viewController";
import { ProjectTaskTrailingControls, TaskDisplayPopover } from "./ProjectTaskTrailingControls";
import { ProjectTaskWorkflowSettings } from "./ProjectTaskWorkflowSettings";
import {
  projectTaskSurfaceView,
  setProjectTaskSurfaceView,
  type ProjectTaskSurfaceView,
} from "./projectTaskBacklogView";

type TaskView = ProjectTaskSurfaceView;

const VIEW_OPTIONS: ReadonlyArray<{
  value: TaskView;
  label: string;
  icon: ComponentType<{ className?: string }>;
}> = [
  { value: "backlog", label: "Backlog", icon: ArchiveIcon },
  { value: "list", label: "List", icon: LayoutListIcon },
  { value: "board", label: "Board", icon: KanbanIcon },
  { value: "timeline", label: "Timeline", icon: CalendarRangeIcon },
];

export interface ProjectTaskToolbarProps {
  readonly viewController: DataViewController;
  readonly composerOpen: boolean;
  readonly onToggleComposer: () => void;
  readonly savedViewsEnabled?: boolean;
  /** Root board tasks for Display → Views/Filters option counts. */
  readonly tasks?: ReadonlyArray<CmmdBoardTask>;
  readonly milestones?: ReadonlyArray<CmmdProjectMilestone>;
  readonly onManageMilestones?: (() => void) | undefined;
  /**
   * `bar` — sticky full-width toolbar inside the tasks surface.
   * `trailing` — compact Linear Filter + Display cluster on the project tabs row.
   */
  readonly variant?: "bar" | "trailing";
  readonly projectId?: number;
  readonly onWorkflowSaved?: (() => void | Promise<void>) | undefined;
  readonly workflowAvailable?: boolean;
  readonly taskMutationsDisabled?: boolean;
}

export function ProjectTaskToolbar(props: ProjectTaskToolbarProps) {
  const variant = props.variant ?? "bar";
  return variant === "trailing" ? (
    <ProjectTaskToolbarTrailing {...props} />
  ) : (
    <ProjectTaskToolbarBar {...props} />
  );
}

function ProjectTaskToolbarTrailing(props: ProjectTaskToolbarProps) {
  const savedViewsEnabled = props.savedViewsEnabled !== false;
  return (
    <div className="hidden items-center gap-1 md:flex">
      <ProjectTaskTrailingControls
        viewController={props.viewController}
        composerOpen={props.composerOpen}
        onToggleComposer={props.onToggleComposer}
        savedViewsEnabled={savedViewsEnabled}
        {...(props.tasks ? { tasks: props.tasks } : {})}
        milestones={props.milestones ?? []}
        onManageMilestones={props.onManageMilestones}
        {...(props.taskMutationsDisabled != null
          ? { taskMutationsDisabled: props.taskMutationsDisabled }
          : {})}
      />
      {props.projectId != null && props.workflowAvailable !== false ? (
        <ProjectTaskWorkflowSettings
          key={props.projectId}
          projectId={props.projectId}
          onSaved={props.onWorkflowSaved}
        />
      ) : null}
    </div>
  );
}

function ProjectTaskToolbarBar(props: ProjectTaskToolbarProps) {
  return (
    <ProjectTaskToolbarBarContent
      {...props}
      displayActive={isTaskDisplayActive(props.viewController)}
    />
  );
}

function isTaskDisplayActive(viewController: DataViewController): boolean {
  return (
    viewController.state.layout !== "list" ||
    viewController.state.statusFilter.length > 0 ||
    viewController.state.ownerFilter.length > 0 ||
    viewController.state.milestoneFilter.length > 0 ||
    viewController.state.taskGroupBy !== "status" ||
    viewController.activeSavedView != null ||
    viewController.isDirty
  );
}

function ProjectTaskToolbarBarContent(
  props: ProjectTaskToolbarProps & { readonly displayActive: boolean },
) {
  const view = projectTaskSurfaceView(props.viewController.state);
  const search = props.viewController.state.search;
  const savedViewsEnabled = props.savedViewsEnabled !== false;
  return (
    <div
      className={cn(
        "sticky top-0 z-10 flex min-h-[64px] flex-col justify-center gap-2 border-b border-border bg-background px-3 py-2 sm:px-4",
        "md:flex-row md:items-center md:gap-3 md:py-0",
      )}
    >
      <PlanningSearchInput
        value={search}
        onChange={(nextSearch) => props.viewController.patchState({ search: nextSearch })}
        placeholder="Search tasks…"
        ariaLabel="Search tasks"
      />
      <div className="hidden min-w-0 flex-1 md:block" />
      <div className="flex items-center gap-2 md:contents">
        <PlanningViewSwitcher
          value={view}
          options={VIEW_OPTIONS}
          onChange={(layout) => setProjectTaskSurfaceView(props.viewController, layout)}
        />
        <TaskDisplayPopover
          viewController={props.viewController}
          savedViewsEnabled={savedViewsEnabled}
          active={props.displayActive}
          tasks={props.tasks ?? []}
          milestones={props.milestones ?? []}
          triggerClassName={RESPONSIVE_ICON_BUTTON_CLASS}
        />
        <div className="relative ml-auto flex items-center gap-2 md:ml-1">
          {props.projectId != null && props.workflowAvailable !== false ? (
            <ProjectTaskWorkflowSettings
              key={props.projectId}
              projectId={props.projectId}
              onSaved={props.onWorkflowSaved}
            />
          ) : null}
          {props.onManageMilestones ? (
            <button
              type="button"
              aria-label="Manage milestones"
              title="Manage milestones"
              onClick={props.onManageMilestones}
              className={cn(
                RESPONSIVE_TEXT_BUTTON_CLASS,
                "inline-flex min-w-touch items-center justify-center gap-1.5 rounded-lg border border-border px-0 text-muted-foreground hover:bg-muted hover:text-foreground md:min-h-9 md:min-w-0 md:rounded-md md:px-3 md:text-sm",
              )}
            >
              <FlagIcon className="size-4 shrink-0" aria-hidden />
              <span className="hidden md:inline">Milestones</span>
            </button>
          ) : null}
          <PlanningNewButton
            label="Task"
            ariaLabel="New task"
            onClick={props.onToggleComposer}
            {...(props.taskMutationsDisabled != null
              ? { disabled: props.taskMutationsDisabled }
              : {})}
          />
        </div>
      </div>
    </div>
  );
}
